import React, { useState, useEffect } from "react";
import {
  Modal,
  Row,
  Typography,
  Col,
  Input,
  Form,
  DatePicker,
  TimePicker,
  message,
} from "antd";
import moment from "moment";

import { platformApi } from "../../../../../../api";
import { withRouter } from "react-router-dom";

const { Item } = Form;
const { Title } = Typography;

const BLOCKED_STATUSES = ["Sent", "Processing", "Failed"];

const RescheduleForm = (props) => {
  const [loading, setLoading] = useState(false);
  const { form, open, close, taskId, taskStatus } = props;
  const { getFieldDecorator, getFieldValue, validateFields, resetFields } = form;

  useEffect(() => {
    if (open && taskStatus && BLOCKED_STATUSES.includes(taskStatus)) {
      message.warning(`Cannot reschedule a task with status ${taskStatus}`);
      close();
    }
  }, [open, taskStatus]);

  const handleSubmit = () => {
    validateFields(async (validationError, values) => {
      if (validationError !== null) {
        message.error("Please fill all required fields");
        return;
      }

      const reData = {
        date: values.date,
        time: values.time,
        name: values.rescheduleName,
      };

      // Existing saved task: persist immediately so cron uses the new time
      if (taskId) {
        if (BLOCKED_STATUSES.includes(taskStatus)) {
          message.error(`Cannot reschedule a task with status ${taskStatus}`);
          return;
        }
        setLoading(true);
        try {
          const res = await platformApi.post("/api/promotions/reschedule", {
            id: taskId,
            executionDate: values.date,
            executionTime: values.time,
            promotionName: values.rescheduleName,
          });
          const { data } = res;
          if (data.code === 200 && data.response && data.response.code === 200) {
            message.success("Task rescheduled");
            props.emitData(reData);
            resetFields();
            props.close();
          } else {
            message.error(
              (data.response && data.response.message) ||
                data.msg ||
                "Failed to reschedule task"
            );
          }
        } catch (err) {
          console.error(err);
          message.error("Failed to reschedule task");
        } finally {
          setLoading(false);
        }
        return;
      }

      // Create flow (not saved yet): update local form state only
      message.success("Schedule updated — Save the task to apply");
      props.emitData(reData);
      resetFields();
      props.close();
    });
  };

  const clearFields = () => {
    resetFields();
  };

  const getDisabledHours = () => {
    var hours = [0, 1, 2, 3, 4, 5, 6, 7, 20, 21, 22, 23];
    const selectedDate = getFieldValue("date");
    const today = moment().startOf("day");

    if (selectedDate && selectedDate.isSame(today, "day")) {
      const currentHour = moment().hour();
      for (let i = 0; i < currentHour; i++) {
        if (!hours.includes(i)) {
          hours.push(i);
        }
      }
    }
    return [...new Set(hours)].sort((a, b) => a - b);
  };

  return (
    <Modal
      title={<Title level={4}>Reschedule</Title>}
      visible={open}
      onCancel={() => {
        clearFields();
        close();
      }}
      onOk={() => handleSubmit()}
      cancelText="Cancel"
      okText="Reschedule"
      confirmLoading={loading}
    >
      <Form>
        <Row type="flex" justify="space-around">
          <Col span={11}>
            <Item label="Date" colon={false}>
              {getFieldDecorator("date", {
                rules: [{ required: true, message: "Please select date" }],
              })(
                <DatePicker
                  format="DD/MM/YYYY"
                  disabledDate={(current) =>
                    current && current < moment().startOf("day")
                  }
                  style={{ width: "100%" }}
                />
              )}
            </Item>
          </Col>
          <Col span={11}>
            <Item label="Time" colon={false}>
              {getFieldDecorator("time", {
                rules: [{ required: true, message: "Please select time" }],
              })(
                <TimePicker
                  defaultOpenValue={moment("00:00", "HH:mm")}
                  format="HH:mm"
                  disabled={!getFieldValue("date")}
                  disabledHours={getDisabledHours}
                  disabledMinutes={(selectedHour) => {
                    if (selectedHour === undefined || selectedHour === null) {
                      return [...Array(60).keys()];
                    }

                    const selectedDate = getFieldValue("date");
                    const today = moment().startOf("day");

                    if (!selectedDate || !selectedDate.isSame(today, "day")) {
                      return [];
                    }

                    const currentHour = moment().hour();
                    const currentMinute = moment().minute();

                    if (selectedHour === currentHour) {
                      const disabledMinutes = [];
                      for (let i = 0; i <= currentMinute; i++) {
                        disabledMinutes.push(i);
                      }
                      return disabledMinutes;
                    }

                    if (selectedHour < currentHour) {
                      return [...Array(60).keys()];
                    }

                    return [];
                  }}
                  style={{ width: "100%" }}
                />
              )}
            </Item>
          </Col>
        </Row>
        <Row type="flex" justify="space-around">
          <Col span={24}>
            <Item label="Reschedule Promotion Name" colon={false}>
              {getFieldDecorator("rescheduleName", {
                rules: [
                  {
                    required: true,
                    message: "Please enter reschedule promotion name",
                  },
                  {
                    pattern: /^[a-zA-Z0-9\s\-.,/]+$/,
                    message:
                      "Only alphanumeric characters, spaces, hyphens, dots, commas, and forward slashes are allowed",
                  },
                ],
              })(
                <Input
                  placeholder="Reschedule Promotion Name"
                  onKeyUp={(e) => {
                    const value = e.target.value.replace(
                      /[^a-zA-Z0-9\s\-.,/]/g,
                      ""
                    );
                    form.setFieldsValue({
                      rescheduleName: value,
                    });
                  }}
                  onInput={(event) => {
                    const rawValue = event.target.value;
                    const filteredValue = rawValue.replace(
                      /[^a-zA-Z0-9\s\-.,/]/g,
                      ""
                    );
                    if (filteredValue !== rawValue) {
                      event.target.value = filteredValue;
                      form.setFieldsValue({
                        rescheduleName: filteredValue,
                      });
                    }
                  }}
                />
              )}
            </Item>
          </Col>
        </Row>
      </Form>
      <br />
    </Modal>
  );
};

const WrappedRescheduleForm = Form.create({ name: "reschedule_form" })(
  RescheduleForm
);
export default withRouter(WrappedRescheduleForm);
